import React from 'react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Card, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Inbox, Send, RefreshCw, Trash2, Download, FileText, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';

const AVATAR_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-orange-500', 'bg-purple-500'];

function stringToColorClass(str) {
    if (!str) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function EmailTable({
    emails,
    loading,
    total,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    onSendNow,
    onRetry,
    onDelete,
    onExportCSV,
    onExportEML,
    showCheckbox = false,
    pageType = 'all' // 'all', 'queued'
}) {
    if (loading) {
        return (
            <Card className="w-full">
                <div className="p-8 text-center text-muted-foreground">Loading emails...</div>
            </Card>
        )
    }

    const totalPages = Math.ceil(total / rowsPerPage);

    return (
        <Card className="w-full shadow-sm">
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {showCheckbox && <TableHead className="w-[50px]"><Checkbox /></TableHead>}
                            <TableHead>Email</TableHead>
                            <TableHead>First Name</TableHead>
                            <TableHead>Last Name</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {emails.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={showCheckbox ? 8 : 7} className="h-24 text-center text-muted-foreground">
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <Inbox className="h-12 w-12 text-muted-foreground/50 mb-2" />
                                        <p>No emails found</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            emails.map((row) => (
                                <TableRow key={row._id}>
                                    {showCheckbox && <TableCell><Checkbox /></TableCell>}
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <Avatar className={`h-8 w-8 ${stringToColorClass(row.email || row.firstName)} text-white`}>
                                                <AvatarFallback className="bg-transparent text-white text-xs">
                                                    {row.firstName?.[0] || row.email?.[0] || 'U'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="font-medium truncate max-w-[200px]">{row.to}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{row.firstName}</TableCell>
                                    <TableCell>{row.lastName}</TableCell>
                                    <TableCell>{row.company}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1 items-start">
                                            <Badge
                                                variant={
                                                    row.isSend ? "success" :
                                                        row.status === 'error' ? "destructive" :
                                                            row.isProcessing ? "warning" : "default"
                                                }
                                            >
                                                {row.isSend ? 'Sent' : (row.status === 'error' ? 'Error' : (row.isProcessing ? 'Processing' : 'Queued'))}
                                            </Badge>
                                            {!row.isSend && row.waitReason && (
                                                <span className="text-[10px] text-blue-600 font-bold max-w-[120px] truncate" title={row.waitReason}>
                                                    {row.waitReason}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {dayjs(row.createdAt).format('DD MMM, YYYY HH:mm')}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <TooltipProvider delayDuration={0}>
                                                {pageType === 'all' && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button variant="ghost" size="icon" onClick={() => onSendNow(row._id)} disabled={row.isSend} className="h-8 w-8">
                                                                <Send className="h-4 w-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Send Now</TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {pageType === 'queued' && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button variant="ghost" size="icon" onClick={() => onRetry(row._id)} disabled={row.isSend} className="h-8 w-8">
                                                                <RefreshCw className="h-4 w-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Retry</TooltipContent>
                                                    </Tooltip>
                                                )}
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>View Details</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={() => onDelete(row)} className="h-8 w-8 text-destructive hover:text-destructive">
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Delete</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={() => onExportCSV(row)} className="h-8 w-8">
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Export CSV</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={() => onExportEML(row)} className="h-8 w-8">
                                                            <FileText className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Export EML</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
            {/* Simple Pagination */}
            <CardFooter className="flex items-center justify-between py-4 border-t">
                <div className="text-sm text-muted-foreground">
                    Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, total)} of {total} results
                </div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(null, page - 1)}
                        disabled={page === 0}
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(null, page + 1)}
                        disabled={page >= totalPages - 1}
                    >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </CardFooter>
        </Card>
    );
} 